/**
 * Shared constants for WayAI Workers
 */

/** Meta (Facebook) Graph API version used for WhatsApp and Instagram integrations */
export const META_API_VERSION = 'v25.0';

// ---------------------------------------------------------------------------
// Tool Execution
// ---------------------------------------------------------------------------

/**
 * Hard ceiling (ms) on a single AI-triggered external/MCP/native tool HTTP fetch
 * during an agent turn. Bounds tool execution so a slow or hung endpoint can't
 * wedge the turn indefinitely (gh #2599).
 *
 * 60s is generous for legitimate slow tool APIs yet well under the eval watchdog
 * default (`EVAL_DEFAULT_RUN_DEADLINE_MS`, 180s): a single hung tool fetch fails
 * fast instead of consuming the whole run budget and falsely reaping a still-
 * progressing eval run. Applied to ALL conversations, not just eval runs.
 */
export const EXTERNAL_TOOL_FETCH_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Harness Control Surface (harness-agents PR 2.7)
// ---------------------------------------------------------------------------

/**
 * The `wayai-conversation` MCP control-surface tool names — the tools an in-sandbox
 * harness agent uses to DRIVE its conversation. SINGLE SOURCE OF TRUTH shared by the
 * MCP registration (`apps/mcp` `control-tools.ts`) and the ConversationDO
 * intent-capture/apply (`apps/backend` harness path), so the two can never drift: a
 * renamed tool the backend matcher stopped recognizing would silently drop a harness
 * agent's close/transfer/state intent.
 */
export const HARNESS_CONTROL_TOOL_NAMES = [
  'end_conversation',
  'transfer_to_agent',
  'transfer_to_team',
  'update_kanban_status',
  'set_state',
] as const;

export type HarnessControlToolName = (typeof HARNESS_CONTROL_TOOL_NAMES)[number];

/**
 * The MCP server-connection name the harness runner registers the WayAI control
 * surface under. The Claude Agent SDK prefixes every tool from a server as
 * `mcp__<serverName>__<tool>`, so a control call arrives in the drained event stream
 * as `mcp__wayai-conversation__end_conversation` etc. Pinned here so the runner
 * mcpServers config (credential injection) and the backend capture matcher agree on
 * the prefix.
 */
export const HARNESS_CONTROL_MCP_SERVER = 'wayai-conversation';

/**
 * The session-scoped secret env var the harness driver injects the minted `way_` MCP-callback token
 * into (harness-agents PR 2.7 injection). The in-sandbox runner reads it to add the reserved control
 * server's `Authorization: Bearer` header at query() time — so the token travels as env, never in the
 * task JSON. The runner is baked into the sandbox template and can't import this constant, so it
 * hardcodes the literal; a drift-guard test asserts the runner references this exact value.
 */
export const HARNESS_MCP_TOKEN_ENV = 'WAYAI_MCP_TOKEN';

/**
 * Per-HUB max concurrent harness sandboxes (harness-agents PR 2.8b). Passed as the
 * `cap` to `hubStub(...).acquireHarnessSlot` at the start of every harness turn so a
 * single hub can't spin up unbounded E2B sandboxes. A conservative placeholder until
 * the plan-tier cap lands (tracked as a deferred follow-up); NOT yet plan-aware.
 */
export const DEFAULT_HARNESS_CONCURRENT_CAP = 3;

// ---------------------------------------------------------------------------
// Authentication Types
// ---------------------------------------------------------------------------

/** Canonical lowercase auth type codes used in seed data, DB storage, and all comparisons. */
export const AUTH_TYPE = {
  OAUTH: 'oauth',
  API_KEY: 'api_key',
  BASIC_AUTH: 'basic_auth',
  BEARER: 'bearer',
  NONE: 'none',
} as const;

export type AuthTypeCode = (typeof AUTH_TYPE)[keyof typeof AUTH_TYPE];

/** Auth types valid for organization credentials (excludes OAuth and None). */
export const ORG_CREDENTIAL_AUTH_TYPES = [
  AUTH_TYPE.API_KEY,
  AUTH_TYPE.BEARER,
  AUTH_TYPE.BASIC_AUTH,
] as const;

export type OrgCredentialAuthType = (typeof ORG_CREDENTIAL_AUTH_TYPES)[number];

/** Maps lowercase code to human-readable display label. */
export const AUTH_TYPE_DISPLAY: Record<string, string> = {
  [AUTH_TYPE.OAUTH]: 'OAuth',
  [AUTH_TYPE.API_KEY]: 'API Key',
  [AUTH_TYPE.BASIC_AUTH]: 'Basic Auth',
  [AUTH_TYPE.BEARER]: 'Bearer Token',
  [AUTH_TYPE.NONE]: 'No Authentication',
};

/** Maps old capitalized display names to canonical lowercase codes. */
const LEGACY_AUTH_TYPE_MAP: Record<string, AuthTypeCode> = {
  'OAuth': AUTH_TYPE.OAUTH,
  'API Key': AUTH_TYPE.API_KEY,
  'Basic Auth': AUTH_TYPE.BASIC_AUTH,
  'Bearer Token': AUTH_TYPE.BEARER,
  'No authentication': AUTH_TYPE.NONE,
  // Also handle the old DISPLAY_TO_CODE bug where 'basic' was used instead of 'basic_auth'
  'basic': AUTH_TYPE.BASIC_AUTH,
};

const VALID_AUTH_TYPES = new Set<string>(Object.values(AUTH_TYPE));

/** Normalizes any auth type string (legacy capitalized or already lowercase) to canonical code. */
export function normalizeAuthType(raw: string): AuthTypeCode {
  if (VALID_AUTH_TYPES.has(raw)) return raw as AuthTypeCode;
  return LEGACY_AUTH_TYPE_MAP[raw] ?? (raw as AuthTypeCode);
}

// ---------------------------------------------------------------------------
// Agent Roles
// ---------------------------------------------------------------------------

// Track-prefixed roles are track-bound; bare roles (`monitor`, `summarizer`,
// `consultant`, the evaluators) are track-independent.
export const AGENT_ROLES = [
  'pilot', 'copilot',
  'pilot_specialist', 'copilot_specialist',
  'pilot_advisor', 'copilot_advisor',
  'monitor', 'conversation_evaluator', 'message_evaluator',
  'summarizer',
  'consultant',
] as const;

export type AgentRole = (typeof AGENT_ROLES)[number];

// `consultant` is deliberately in NONE of the three groups below: it is
// track-independent (never a pilot/copilot responder) yet foreground — consulted
// on demand by people (and agents) in visible threads, and its turns bill as
// normal operations. An advisor advises an AI mid-turn and is invisible; a
// consultant is consulted by people (and agents) in visible threads.
export const BACKGROUND_AGENT_ROLES: readonly AgentRole[] = ['conversation_evaluator', 'message_evaluator', 'monitor', 'summarizer'];
export const PILOT_TRACK_ROLES: readonly AgentRole[] = ['pilot', 'pilot_specialist'];
export const COPILOT_TRACK_ROLES: readonly AgentRole[] = ['copilot', 'copilot_specialist'];

/** Maps agent role to human-readable display label. */
export const AGENT_ROLE_DISPLAY: Record<AgentRole, string> = {
  pilot: 'Pilot',
  copilot: 'Copilot',
  pilot_specialist: 'Pilot Specialist',
  copilot_specialist: 'Copilot Specialist',
  pilot_advisor: 'Pilot Advisor',
  copilot_advisor: 'Copilot Advisor',
  monitor: 'Monitor',
  conversation_evaluator: 'Conversation Evaluator',
  message_evaluator: 'Message Evaluator',
  summarizer: 'Summarizer',
  consultant: 'Consultant',
};

// Role-picker disambiguation helper text (advisor vs. consultant) is user-facing
// UI prose, so it lives in the web next-intl bags (`settings.roleDescription.*`,
// all three locales) — NOT here. Role *labels* above stay core-sourced because
// they are stable product vocabulary shared RN-safe; explanatory sentences are
// translatable copy and must go through the i18n pipeline (AGENTS.md Frontend:
// all three locales).

// ---------------------------------------------------------------------------
// AI Modes
// ---------------------------------------------------------------------------

export const AI_MODES = ['pilot', 'copilot', 'pilot+copilot', 'turned_off'] as const;

export type AiMode = (typeof AI_MODES)[number];

export const COPILOT_MODES: readonly AiMode[] = ['pilot+copilot', 'copilot'];

/**
 * Modes that have a pilot — the AI answers the end user directly, so the
 * conversation starts on the Agent track. The complement (`copilot`,
 * `turned_off`) has no pilot: the AI either advises the support team or is off,
 * and channel delivery is never correct, so those start on the Team track.
 */
export const PILOT_MODES: readonly AiMode[] = ['pilot', 'pilot+copilot'];

/** Maps AI mode to human-readable display label. */
export const AI_MODE_DISPLAY: Record<AiMode, string> = {
  pilot: 'Pilot',
  copilot: 'Copilot',
  'pilot+copilot': 'Pilot+Copilot',
  turned_off: 'Turned Off',
};

// ---------------------------------------------------------------------------
// Identifier validation
// ---------------------------------------------------------------------------

/**
 * Injection-safe whitelist for `user_id` values that may appear in URL paths,
 * R2 key prefixes, ScheduleSession scope keys, and interpolated SQL. Accepts
 * both HubDO-generated UUIDs and WorkOS-style ids (e.g. `user_01KMR9AZ...`)
 * while excluding every character that could break out of a single-quoted SQL
 * literal or create structural ambiguity in `:`-delimited keys.
 */
export const SAFE_USER_ID_REGEX = /^[A-Za-z0-9_-]{1,64}$/;

// ---------------------------------------------------------------------------
// Supported UI locales — proxy-to-backend auth-locale contract
// ---------------------------------------------------------------------------

// Single source of truth for frontend cookie + backend header locale validation.
export const SUPPORTED_LOCALES = ['en', 'pt', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Header set by the same-origin proxy from the auth-locale cookie. */
export const AUTH_LOCALE_HEADER = 'x-wayai-auth-locale';

// ---------------------------------------------------------------------------
// Outbound webhook signature headers (API channel connector)
// ---------------------------------------------------------------------------

/**
 * Signature headers on an outbound API-channel webhook POST. The receiver recomputes
 * `HMAC-SHA256(webhook_signing_secret, "{timestamp}:{body}")` (lowercase hex) and
 * constant-time-compares it against the `v1=`-prefixed value. Single source of truth so
 * the signer (`utils/webhook-signature.ts`) and the receiver docs can't drift. Lowercase
 * per the `x-wayai-auth-locale` precedent.
 */
export const WEBHOOK_TIMESTAMP_HEADER = 'x-wayai-timestamp';
export const WEBHOOK_SIGNATURE_HEADER = 'x-wayai-signature';

/**
 * Documented command to install the WayAI skill into an AI coding harness.
 * Single source of truth for the TS surfaces that render it — apps/web
 * (`INSTALL_COMMAND`), apps/cli (`SKILL_INSTALL_CMD`), apps/mcp. Docs that can't
 * import TS (`skills/wayai/SKILL.md`, the public README) carry the string by hand.
 *
 * `mkdir -p .claude` is load-bearing, not cosmetic: the external `skills` CLI
 * (`npx skills add`) creates Claude Code's `.claude/skills/wayai` symlink ONLY
 * when `.claude/` already exists, else it silently skips the link (printing
 * "symlinked: Claude Code" anyway). Pre-creating `.claude/` guarantees it;
 * harmless for other harnesses (they read `.agents/skills/`). Remove the prefix
 * once the upstream `skills` CLI creates the parent dir itself (vercel-labs/skills).
 */
export const SKILL_INSTALL_COMMAND =
  'mkdir -p .claude && npx skills add wayai-pro/wayai-skill -y';
